import React from "react";
import {css} from "@panda/css";

import {CalendarCheckIcon, PropertyPlanIcon} from "@pg-design/icons-module";
import {Highlight, Text} from "@pg-design/text-module";
import {quarterToRomanNumerals} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {useResponsiveOpenLink} from "../../../hooks/use_responsive_open_link";
import {ILatestQuery} from "../../../utils/latest_query";
import {useOfferDetails} from "../../hooks/use_offer_details";
import {IOfferBoxOffer} from "../../types/IOfferBoxOffer";
import {getOfferCardSubtitle, getOfferCardTitle} from "../../utils/get_offer_card_headings";
import {NoTagPlug} from "./NoTagPlug";
import {OfferBoxUspSection} from "./OfferBoxUspSection";
import {OfferToggleDescription} from "./OfferToggleDescription";

interface IProps {
    offer: IOfferBoxOffer;
    offerLink: string;
    isArchived?: boolean;
    isUnavailable?: boolean;
    unlinkVendor?: boolean;
    isInfoPanelOpened?: boolean;
    titleTag?: React.ElementType;
    latestQuery?: ILatestQuery & Record<string, string | string[] | null>;
    displayAddress?: string;
    onInfoPanelCloseClick: (event: React.MouseEvent) => void;
    onOfferLinkClick?: () => string | void;
    onLogoClick?: () => void;
}

export const OfferBoxUspInfoSection = (props: IProps) => {
    const {offer, offerLink, isInfoPanelOpened, onInfoPanelCloseClick, titleTag} = props;
    const target = useResponsiveLinkTarget();
    const openResponsiveOfferLink = useResponsiveOpenLink(offerLink);

    const {constructionEndDate, priceValueToShow, priceM2ValueToShow, priceUnit, roomText, areaText} = useOfferDetails(props);

    const title = props.displayAddress ?? getOfferCardTitle(offer);
    const subtitle = getOfferCardSubtitle(offer);
    const roomsAreaText = `${roomText} | Metraż ${areaText}`;
    const handoverText = getHandoverText(constructionEndDate);

    const getOfferLinkForClick = () => props.onOfferLinkClick?.() ?? offerLink;

    const handleOfferLinkClick = () => {
        openResponsiveOfferLink(getOfferLinkForClick());
    };

    const handleOfferAnchorMouseDown = usePrimaryClick((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.currentTarget.href = getOfferLinkForClick();
    });

    return (
        <div className={wrapperStyle}>
            {offer.description && (
                <OfferToggleDescription show={!!offer.description && isInfoPanelOpened} truncateLine={8} fullHeight onClose={onInfoPanelCloseClick}>
                    {offer.description}
                </OfferToggleDescription>
            )}

            <div className={headingsStyle}>
                <Text variant="headline_6" as={titleTag || "p"} className={ellipsisStyle}>
                    {title}
                </Text>
                <a
                    href={offerLink}
                    title={subtitle}
                    target={target}
                    onMouseDown={handleOfferAnchorMouseDown}
                    className={offerLinkStyle}
                    data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_TILE_OFFER_NAME}
                >
                    <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)" className={ellipsisStyle}>
                        {subtitle}
                    </Text>
                </a>
            </div>

            <OfferBoxUspSection offer={offer} />

            <div onClick={handleOfferLinkClick} className={featuresStyle}>
                <div className={featureRowStyle}>
                    <div className={iconWrapperStyle}>
                        <PropertyPlanIcon size="1.6" />
                    </div>
                    <Text variant="info_txt_1">{roomsAreaText}</Text>
                </div>
                {handoverText && (
                    <div className={featureRowStyle}>
                        <div className={iconWrapperStyle}>
                            <CalendarCheckIcon size="1.6" />
                        </div>
                        <Text variant="info_txt_1">{handoverText}</Text>
                    </div>
                )}
            </div>

            <div onClick={handleOfferLinkClick} className={priceStyle}>
                {priceValueToShow ? (
                    <>
                        <Text variant="headline_4" as="span">
                            <Highlight>
                                <Text variant="body_copy_2" as="span" strong className={css({pr: 0.5})}>
                                    od
                                </Text>
                                {priceValueToShow}
                                <Text variant="body_copy_2" as="span" strong className={css({pl: 0.5})}>
                                    {priceUnit}
                                </Text>
                            </Highlight>
                        </Text>
                        {priceM2ValueToShow && (
                            <Text variant="body_copy_2" as="span" strong color="var(--colors-gray-800)" className={priceM2Style}>
                                {priceM2ValueToShow}{" "}
                                <Text variant="info_txt_1" as="span" strong color="var(--colors-gray-800)">
                                    {priceUnit}/m2
                                </Text>
                            </Text>
                        )}
                    </>
                ) : (
                    <NoTagPlug height="2.8rem" />
                )}
            </div>
        </div>
    );
};

const getHandoverText = (constructionEndDate: string): string | null => {
    if (!constructionEndDate) {
        return null;
    }
    if (constructionEndDate === "gotowe") {
        return "Gotowe do odbioru";
    }
    return `Oddanie: ${quarterToRomanNumerals(constructionEndDate, "kwartał")}`;
};

const wrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: 2,
    pt: 2,
    pr: 2,
    pb: 0,
    pl: 2,
    position: "relative"
});

const headingsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    overflow: "hidden"
});

const offerLinkStyle = css({
    _hover: {
        color: "unset"
    }
});

const ellipsisStyle = css({
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
});

const featuresStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    cursor: "pointer"
});

const featureRowStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 0.5
});

const iconWrapperStyle = css({
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "2.4rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "2.4rem",
    height: "2.4rem"
});

const priceStyle = css({
    display: "flex",
    alignItems: "flex-end",
    marginTop: "auto",
    cursor: "pointer"
});

const priceM2Style = css({
    ml: 2
});
import React from "react";
import {css} from "@panda/css";

import {CalendarCheckIcon, PropertyPlanIcon} from "@pg-design/icons-module";
import {Highlight, Text} from "@pg-design/text-module";
import {quarterToRomanNumerals} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {useResponsiveOpenLink} from "../../../hooks/use_responsive_open_link";
import {ILatestQuery} from "../../../utils/latest_query";
import {useOfferDetails} from "../../hooks/use_offer_details";
import {IOfferBoxOffer} from "../../types/IOfferBoxOffer";
import {getOfferCardSubtitle, getOfferCardTitle} from "../../utils/get_offer_card_headings";
import {NoTagPlug} from "./NoTagPlug";
import {OfferBoxUspSection} from "./OfferBoxUspSection";
import {OfferToggleDescription} from "./OfferToggleDescription";

interface IProps {
    offer: IOfferBoxOffer;
    offerLink: string;
    isArchived?: boolean;
    isUnavailable?: boolean;
    unlinkVendor?: boolean;
    isInfoPanelOpened?: boolean;
    titleTag?: React.ElementType;
    latestQuery?: ILatestQuery & Record<string, string | string[] | null>;
    displayAddress?: string;
    onInfoPanelCloseClick: (event: React.MouseEvent) => void;
    onOfferLinkClick?: () => string | void;
    onLogoClick?: () => void;
}

export const OfferBoxUspInfoSection = (props: IProps) => {
    const {offer, offerLink, isInfoPanelOpened, onInfoPanelCloseClick, titleTag} = props;
    const target = useResponsiveLinkTarget();
    const openResponsiveOfferLink = useResponsiveOpenLink(offerLink);

    const {constructionEndDate, priceValueToShow, priceM2ValueToShow, priceUnit, roomText, areaText} = useOfferDetails(props);

    const title = props.displayAddress ?? getOfferCardTitle(offer);
    const subtitle = getOfferCardSubtitle(offer);
    const roomsAreaText = `${roomText} | Metraż ${areaText}`;
    const handoverText = getHandoverText(constructionEndDate);

    const getOfferLinkForClick = () => props.onOfferLinkClick?.() ?? offerLink;

    const handleOfferLinkClick = () => {
        openResponsiveOfferLink(getOfferLinkForClick());
    };

    const handleOfferAnchorMouseDown = usePrimaryClick((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.currentTarget.href = getOfferLinkForClick();
    });

    return (
        <div className={wrapperStyle}>
            {offer.description && (
                <OfferToggleDescription show={!!offer.description && isInfoPanelOpened} truncateLine={8} fullHeight onClose={onInfoPanelCloseClick}>
                    {offer.description}
                </OfferToggleDescription>
            )}

            <div className={headingsStyle}>
                <Text variant="headline_6" as={titleTag || "p"} className={ellipsisStyle}>
                    {title}
                </Text>
                <a
                    href={offerLink}
                    title={subtitle}
                    target={target}
                    onMouseDown={handleOfferAnchorMouseDown}
                    className={offerLinkStyle}
                    data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_TILE_OFFER_NAME}
                >
                    <Text variant="info_txt_1" as="p" color="var(--colors-gray-700)" className={ellipsisStyle}>
                        {subtitle}
                    </Text>
                </a>
            </div>

            <OfferBoxUspSection offer={offer} />

            <div onClick={handleOfferLinkClick} className={featuresStyle}>
                <div className={featureRowStyle}>
                    <div className={iconWrapperStyle}>
                        <PropertyPlanIcon size="1.6" />
                    </div>
                    <Text variant="info_txt_1">{roomsAreaText}</Text>
                </div>
                {handoverText && (
                    <div className={featureRowStyle}>
                        <div className={iconWrapperStyle}>
                            <CalendarCheckIcon size="1.6" />
                        </div>
                        <Text variant="info_txt_1">{handoverText}</Text>
                    </div>
                )}
            </div>

            <div onClick={handleOfferLinkClick} className={priceStyle}>
                {priceValueToShow ? (
                    <>
                        <Text variant="headline_4" as="span">
                            <Highlight>
                                <Text variant="body_copy_2" as="span" strong className={css({pr: 0.5})}>
                                    od
                                </Text>
                                {priceValueToShow}
                                <Text variant="body_copy_2" as="span" strong className={css({pl: 0.5})}>
                                    {priceUnit}
                                </Text>
                            </Highlight>
                        </Text>
                        {priceM2ValueToShow && (
                            <Text variant="body_copy_2" as="span" strong color="var(--colors-gray-800)" className={priceM2Style}>
                                {priceM2ValueToShow}{" "}
                                <Text variant="info_txt_1" as="span" strong color="var(--colors-gray-800)">
                                    {priceUnit}/m2
                                </Text>
                            </Text>
                        )}
                    </>
                ) : (
                    <NoTagPlug height="2.8rem" />
                )}
            </div>
        </div>
    );
};

const getHandoverText = (constructionEndDate: string): string | null => {
    if (!constructionEndDate) {
        return null;
    }
    if (constructionEndDate === "gotowe") {
        return "Gotowe do odbioru";
    }
    return `Oddanie: ${quarterToRomanNumerals(constructionEndDate, "kwartał")}`;
};

const wrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: 2,
    pt: 2,
    pr: 2,
    pb: 0,
    pl: 2,
    position: "relative"
});

const headingsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    overflow: "hidden"
});

const offerLinkStyle = css({
    _hover: {
        color: "unset"
    }
});

const ellipsisStyle = css({
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
});

const featuresStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    cursor: "pointer"
});

const featureRowStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 0.5
});

const iconWrapperStyle = css({
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "2.4rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "2.4rem",
    height: "2.4rem"
});

const priceStyle = css({
    display: "flex",
    alignItems: "flex-end",
    marginTop: "auto",
    cursor: "pointer"
});

const priceM2Style = css({
    ml: 2
});
